import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { FileCopy, ArrowBack } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("script_load"));
    document.body.appendChild(s);
  });
}

function normalizeMetaTokens(data) {
  if (!data || typeof data !== "object") return null;
  const hasPages = Array.isArray(data.pages) && data.pages.length > 0;
  const facebookUserId = String(
    data.facebookUserId ??
      data.user_id ??
      data.facebook_user_id ??
      data.userId ??
      (!hasPages ? data.id : null) ??
      ""
  ).trim();
  const facebookUserToken = String(
    data.facebookUserToken ??
      data.user_access_token ??
      data.access_token ??
      data.accessToken ??
      data.token ??
      ""
  ).trim();
  if (!facebookUserId || !facebookUserToken) return null;
  return { facebookUserId, facebookUserToken };
}

/**
 * Facebook / Instagram / Telegram via ConectHUB — usa o mesmo embed.js oficial:
 * CoExConnect.open → popup em /connect/{channel}/{accountId}?embed=true&...
 * (documentação: https://app.conecthub360.com.br/docs )
 */
function ConecthubMetaEmbedFlowModal({
  open,
  onClose,
  onBack,
  channel,
  onCompleted,
}) {
  const [phase, setPhase] = useState("idle");
  const [errorText, setErrorText] = useState("");
  const [embedConfig, setEmbedConfig] = useState(null);
  const [payloadRecoveryJson, setPayloadRecoveryJson] = useState(null);
  const telegramConnectDoneRef = useRef(false);

  const isTelegram = channel === "telegram";

  const reset = useCallback(() => {
    setPhase("idle");
    setErrorText("");
    setEmbedConfig(null);
    setPayloadRecoveryJson(null);
    telegramConnectDoneRef.current = false;
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      telegramConnectDoneRef.current = false;
      return;
    }

    const run = async () => {
      setPhase("loading");
      setErrorText("");
      try {
        const { data: cfg } = await api.get("/companies/conecthub-embed-config");
        const accountId = (cfg.accountId || "").trim();
        if (!accountId) {
          setErrorText(i18n.t("conecthubMeta.errors.masterAccount"));
          setPhase("error");
          return;
        }
        if (!isTelegram && (!cfg.metaWebhookUrl || !cfg.metaCallbackUrl)) {
          setErrorText(i18n.t("conecthubMeta.errors.backendUrl"));
          setPhase("error");
          return;
        }
        setEmbedConfig(cfg);
        setPhase("webhook");
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.message ||
          i18n.t("conecthubMeta.errors.generic");
        setErrorText(msg);
        setPhase("error");
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channel]);

  useEffect(() => {
    if (!open || !isTelegram || phase !== "embed") {
      return undefined;
    }
    const onMessage = (event) => {
      if (event.data?.type !== "COEX_CONNECTED" || !event.data?.payload) {
        return;
      }
      if (telegramConnectDoneRef.current) {
        return;
      }
      const data = event.data.payload;
      (async () => {
        try {
          telegramConnectDoneRef.current = true;
          await api.post("/whatsapp/conecthub-telegram", data);
          toast.success(i18n.t("conecthubMeta.successTelegram"));
          if (onCompleted) onCompleted();
          onClose();
        } catch (err) {
          telegramConnectDoneRef.current = false;
          const msg =
            err.response?.data?.error ||
            err.message ||
            i18n.t("conecthubMeta.errors.save");
          toast.error(msg);
          try {
            setPayloadRecoveryJson(JSON.stringify(data, null, 2));
          } catch {
            setPayloadRecoveryJson(String(data));
          }
          setErrorText(msg);
          setPhase("payloadRecovery");
        }
      })();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [open, isTelegram, phase, onCompleted, onClose]);

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(i18n.t("conecthubEmbed.copied")),
      () => toast.error(i18n.t("conecthubEmbed.copyFail"))
    );
  };

  const handleCopyPayload = () => {
    if (!payloadRecoveryJson) return;
    navigator.clipboard.writeText(payloadRecoveryJson).then(
      () => toast.success(i18n.t("conecthubEmbed.payloadCopied")),
      () => toast.error(i18n.t("conecthubEmbed.copyFail"))
    );
  };

  const handleOpenEmbed = async () => {
    if (!embedConfig) return;
    const hubChannel = isTelegram ? "telegram" : channel === "instagram" ? "instagram" : "facebook";
    const addInstagram = hubChannel === "instagram";
    setPhase("opening");

    try {
      await loadScriptOnce(embedConfig.embedScriptUrl);
      const CoEx = window.CoExConnect;
      if (!CoEx || typeof CoEx.open !== "function") {
        throw new Error("no_coex");
      }

      const openOpts = {
        accountId: embedConfig.accountId,
        channel: hubChannel,
        onSuccess: async (data) => {
          if (isTelegram) {
            if (telegramConnectDoneRef.current) {
              return;
            }
            try {
              telegramConnectDoneRef.current = true;
              await api.post("/whatsapp/conecthub-telegram", data);
              toast.success(i18n.t("conecthubMeta.successTelegram"));
              if (onCompleted) onCompleted();
              onClose();
            } catch (err) {
              telegramConnectDoneRef.current = false;
              const msg =
                err.response?.data?.error ||
                err.message ||
                i18n.t("conecthubMeta.errors.save");
              toast.error(msg);
              try {
                setPayloadRecoveryJson(JSON.stringify(data, null, 2));
              } catch {
                setPayloadRecoveryJson(String(data));
              }
              setErrorText(msg);
              setPhase("payloadRecovery");
            }
            return;
          }

          const tokens = normalizeMetaTokens(data);
          try {
            await api.post("/facebook", {
              addInstagram,
              ...(data && typeof data === "object" ? data : {}),
              ...(tokens && {
                facebookUserId: tokens.facebookUserId,
                facebookUserToken: tokens.facebookUserToken,
              }),
            });
            toast.success(
              addInstagram
                ? i18n.t("conecthubMeta.successInstagram")
                : i18n.t("conecthubMeta.successFacebook")
            );
            if (onCompleted) onCompleted();
            onClose();
          } catch (err) {
            const msg =
              err.response?.data?.error ||
              err.message ||
              i18n.t("conecthubMeta.errors.save");
            toast.error(msg);
            try {
              setPayloadRecoveryJson(JSON.stringify(data, null, 2));
            } catch {
              setPayloadRecoveryJson(String(data));
            }
            setErrorText(msg);
            setPhase("payloadRecovery");
          }
        },
        onError: (err) => {
          console.error(err);
          if (err === "Popup blocked by browser") {
            toast.error(i18n.t("conecthubMeta.errors.popupBlocked"));
          } else {
            toast.error(i18n.t("conecthubEmbed.errors.embed"));
          }
          setPhase("webhook");
        },
      };

      if (isTelegram && embedConfig.telegramMtprotoWebhookUrl) {
        openOpts.webhookUrl = embedConfig.telegramMtprotoWebhookUrl;
      } else if (!isTelegram && embedConfig.metaWebhookUrl) {
        openOpts.webhookUrl = embedConfig.metaWebhookUrl;
      }
      if (embedConfig.metaCallbackUrl) {
        openOpts.callbackUrl = embedConfig.metaCallbackUrl;
      }

      CoEx.open(openOpts);
      setPhase("embed");
    } catch (e) {
      toast.error(i18n.t("conecthubEmbed.errors.script"));
      setPhase("webhook");
    }
  };

  const titleKey =
    channel === "instagram"
      ? "conecthubMeta.titleInstagram"
      : channel === "telegram"
        ? "conecthubMeta.titleTelegram"
        : "conecthubMeta.titleFacebook";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={payloadRecoveryJson ? "md" : "sm"}
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          {onBack ? (
            <IconButton
              edge="start"
              onClick={onBack}
              aria-label="back"
              style={{ marginRight: 8 }}
            >
              <ArrowBack />
            </IconButton>
          ) : null}
          <span>
            {phase === "payloadRecovery"
              ? i18n.t("conecthubEmbed.payloadRecoveryTitle")
              : i18n.t(titleKey)}
          </span>
        </Box>
      </DialogTitle>
      <DialogContent>
        {phase === "loading" && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        )}

        {phase === "error" && (
          <Typography color="error">{errorText}</Typography>
        )}

        {phase === "webhook" && embedConfig && (
          <Box>
            <Box
              mb={2}
              p={1.5}
              borderRadius={4}
              style={{
                backgroundColor: "rgba(59, 130, 246, 0.1)",
                border: "1px solid rgba(59, 130, 246, 0.35)",
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 600 }} gutterBottom>
                {i18n.t("conecthubMeta.hubChannelNoticeTitle")}
              </Typography>
              <Typography variant="caption" color="textSecondary" component="p">
                {i18n.t("conecthubMeta.hubChannelNoticeBody")}
              </Typography>
            </Box>
            {isTelegram && (
              <>
                <Typography variant="body2" gutterBottom>
                  {i18n.t("conecthubMeta.telegramHelp")}
                </Typography>
                {embedConfig.telegramDirectLink ? (
                  <Box mb={2}>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      {embedConfig.telegramDirectLinkWithWebhook
                        ? i18n.t("conecthubMeta.telegramDirectLinkWithWebhookLabel")
                        : i18n.t("conecthubMeta.telegramDirectLinkLabel")}
                    </Typography>
                    {embedConfig.telegramDirectLinkWithWebhook ? (
                      <Typography variant="caption" color="textSecondary" display="block" paragraph>
                        {i18n.t("conecthubMeta.telegramDirectLinkWithWebhookHint")}
                      </Typography>
                    ) : null}
                    <TextField
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      value={
                        embedConfig.telegramDirectLinkWithWebhook ||
                        embedConfig.telegramDirectLink
                      }
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              onClick={() =>
                                handleCopy(
                                  embedConfig.telegramDirectLinkWithWebhook ||
                                    embedConfig.telegramDirectLink
                                )
                              }
                              size="small"
                            >
                              <FileCopy fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                ) : null}
                {embedConfig.telegramMtprotoWebhookUrl ? (
                  <Box mb={2}>
                    <Typography variant="body2" gutterBottom>
                      {i18n.t("conecthubMeta.telegramInboundWebhookHelp")}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      {i18n.t("conecthubMeta.telegramInboundWebhookLabel")}
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      value={embedConfig.telegramMtprotoWebhookUrl}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              onClick={() =>
                                handleCopy(embedConfig.telegramMtprotoWebhookUrl)
                              }
                              size="small"
                            >
                              <FileCopy fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                ) : (
                  <Typography variant="caption" color="textSecondary" display="block" paragraph>
                    {i18n.t("conecthubMeta.telegramWebhookOptional")}
                  </Typography>
                )}
              </>
            )}
            {!isTelegram && embedConfig.metaWebhookUrl ? (
              <>
                <Typography variant="body2" gutterBottom>
                  {i18n.t("conecthubMeta.webhookHelp")}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                  {i18n.t("conecthubMeta.webhookLabel")}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  value={embedConfig.metaWebhookUrl}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => handleCopy(embedConfig.metaWebhookUrl)}
                          size="small"
                        >
                          <FileCopy fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            ) : null}
            <Box mt={2}>
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                {i18n.t("conecthubMeta.callbackLabel")}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                margin="dense"
                value={embedConfig.metaCallbackUrl || ""}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => handleCopy(embedConfig.metaCallbackUrl)}
                        size="small"
                      >
                        <FileCopy fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenEmbed}
                disabled={phase === "opening"}
              >
                {phase === "opening" ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  i18n.t("conecthubEmbed.openEmbed")
                )}
              </Button>
            </Box>
          </Box>
        )}

        {phase === "embed" && (
          <Typography variant="body2">
            {i18n.t("conecthubEmbed.embedRunning")}
          </Typography>
        )}

        {phase === "payloadRecovery" && payloadRecoveryJson && (
          <Box>
            <Typography variant="body2" color="error" paragraph>
              {errorText || i18n.t("conecthubEmbed.payloadRecoveryHelp")}
            </Typography>
            <TextField
              fullWidth
              multiline
              variant="outlined"
              margin="dense"
              value={payloadRecoveryJson}
              InputProps={{
                readOnly: true,
                style: { fontFamily: "monospace", fontSize: 12 },
              }}
              rows={14}
            />
            <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<FileCopy />}
                onClick={handleCopyPayload}
              >
                {i18n.t("conecthubEmbed.copyPayload")}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {i18n.t("conecthubEmbed.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConecthubMetaEmbedFlowModal;
